import React, { FC, useContext, useImperativeHandle } from 'react'
import { withApollo } from 'react-apollo'

import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useLazyQuery } from '@apollo/react-hooks'
import { pathOr } from 'ramda'
import { compose } from 'recompose'

import { USER_EMAIL } from 'app-constants'
import { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { userHasViewedExperiment } from 'pages/DirectToMobileLander/utils'
import { TestIDs } from 'test/utils/testIds'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import {
  IErrorModalProps,
  withApolloErrorHandler,
  withApolloErrorModal,
} from 'utils/Apollo/apolloErrorHandlingProvider'
import { EnvironmentProps, withEnv } from 'utils/environment'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { webToAppV2Experiment } from 'utils/Optimizely/features'
import { PromptProps, withPrompt } from 'utils/prompts'
import { createExtoleProfile } from 'utils/referral'

import { generateErrorCodeMap } from '../errorCodeMap'
import { errorModal } from '../utils'
import { FormValues } from './form'
import emailAddressValidationQuery from './gql/emailAddressValidation.query.gql'
import { VerifyEmailModal } from './prompts'
import { StyledButton } from './styled-components'
import { EmailValidationQuery } from './types'
import { emailTypoMessage } from './validation'

export interface ButtonHandle {
  click: () => void
}

interface InnerProps {
  formValues: FormValues
  getSourceAttributes: any
  isEmailValid: boolean
  isProspectOnDirectToMobile: boolean
  isSubmitting: boolean
  setErrors: any
  setIsEmailRowDisplayed: (state: boolean) => void
  setIsEmailValid: (state: boolean) => void
  setSubmitEmailCount: (state: number) => void
  submitEmailCount: number
  validateForm: any
  inputRef: any
}

type Props = InnerProps &
  EnvironmentProps &
  IErrorModalProps &
  WithPartnerProps &
  WithFlowRouterProps &
  WithOptimizelyProps &
  PromptProps

const enhance = compose(
  withApolloErrorModal,
  withPrompt,
  withFlowRouter,
  withEnv,
  withApolloErrorHandler('EmailSubmitSession', () => () => false),
  withApollo,
  withOptimizely,
)

const Button: FC<Props> = (props: Props) => {
  const { isDynamicFunnelEnabled, registerProspect, routeToPage } = useContext(FunnelContext)
  const { getRemoteString, isLoadingRemoteStrings } = useRemoteStrings()
  const [validateEmail, { client: apolloClient }] = useLazyQuery<EmailValidationQuery>(
    emailAddressValidationQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      onCompleted: data => onValidationComplete(data),
      onError: error => onError(error),
    },
  )
  const checkIfExperimentViewedAndSend = (): void => {
    const variation = props.getVariation(webToAppV2Experiment)
    if (variation && userHasViewedExperiment(variation)) {
      const experimentConfig = props.getOptimizelyExperimentConfig(webToAppV2Experiment)
      const variationConfig = experimentConfig?.variationsMap[variation]
      if (variationConfig) {
        trackEvent('Experiment Viewed', {
          eventData: {
            object_name: 'optimizelyExperiment',
            experimentName: experimentConfig.key,
            experimentId: experimentConfig.id,
            variationId: variationConfig.id,
            variationName: variationConfig.key,
          },
        })
      }
    }
  }
  const onPress = async () => {
    // Keep count of number of email submissions
    props.setSubmitEmailCount(props.submitEmailCount + 1)
    const errorsOnFormValidate = await props.validateForm()
    if (!errorsOnFormValidate.email) {
      props.setErrors({})
      window.sessionStorage.setItem(USER_EMAIL, props.formValues.email)
      const variables = { email: props.formValues.email }
      try {
        // Apollo queries don't emit `onComplete` handler if query results hit cache
        const cacheHit = apolloClient?.readQuery({ query: emailAddressValidationQuery, variables })
        !cacheHit ? validateEmail({ variables }) : onValidationComplete(cacheHit)
      } catch (error) {
        validateEmail({ variables })
      }
    } else {
      props.setIsEmailValid(false)
      props.setErrors({ email: errorsOnFormValidate.email })
    }

    // Track button tap for analytics
    trackEvent('Button Tapped', {
      eventData: {
        ...getButtonTappedEvent(TRACKER_IDS.SIGNUP.GET_STARTED_BUTTON),
        ...getDefaultEventData('Button Tapped'),
        email: props.formValues.email,
      },
      flow: props.isProspectOnDirectToMobile ? 'directToMobile' : props.flow,
      partner: props.partner,
    })
  }
  const handleEmailValidationSubmission = async () => {
    const sourceAttributes = props.getSourceAttributes()
    await registerProspect(
      props.formValues.email,
      sourceAttributes.raw.s1,
      JSON.stringify(sourceAttributes.raw),
    )
    createExtoleProfile(props.env, props.formValues.email, sourceAttributes.raw.code)
  }
  const onValidationComplete = ({
    emailAddressValidation: { credentialsExist, possibleTypo, suggestion },
  }) => {
    // If credentials don't exist - prompt user to double check email
    // Otherwise, display credentials taken error modal
    if (!credentialsExist) {
      props
        .promptUser(
          VerifyEmailModal({
            handleChange: () =>
              possibleTypo && props.setErrors({ email: emailTypoMessage(suggestion) }),
            handleOk: () =>
              handleEmailValidationSubmission().then(() => {
                checkIfExperimentViewedAndSend()
                routeUser()
              }),
            strings: {
              title: getRemoteString({ key: 'create_account.email_confirmation.title' }),
              description: getRemoteString({
                key: 'create_account.email_confirmation.body',
                variables: [props.formValues.email],
              }),
              primaryLabel: getRemoteString({ key: 'create_account.email_confirmation.confirm' }),
              secondaryLabel: getRemoteString({ key: 'create_account.email_confirmation.cancel' }),
            },
            partner: props.partner,
          }),
        )
        .then(result => {
          if (result.handler) {
            result.handler()
          }
        })
    } else {
      const errorCodeMap = generateErrorCodeMap(
        {
          env: props.env,
          closeErrorModal: props.closeErrorModal,
          partner: props.partner,
          isProspectOnDirectToMobile: props.isProspectOnDirectToMobile,
        },
        props.formValues,
      )
      const errorCode = 'email_has_already_been_taken'
      const { title, message, actions } = errorCodeMap.get(errorCode)
      props.showErrorModal(errorModal('', title, message, actions, errorCode))
    }
  }
  const onError = error => {
    const retryHandler = () => props.closeErrorModal()
    const errorCode = pathOr(
      null,
      ['graphQLErrors', 0, 'extensions', 'response', 'body', 'errors', 0],
      error,
    )
    const customErrorCode =
      error.graphQLErrors?.[0]?.name === 'EmailValidationRateExceededError' ? 429 : undefined
    const { title, message, actions, image } = getGeneralError(
      error,
      props,
      retryHandler,
      customErrorCode,
    )
    props.showErrorModal(
      errorModal(props, title, message, actions, customErrorCode ?? errorCode, image),
    )
  }
  const routeUser = () => {
    if (props.isProspectOnDirectToMobile) {
      isDynamicFunnelEnabled
        ? routeToPage(PageName.Signup, ActionType.Continue)
        : props.route({ from: FlowRoute.Signup, action: 'direct-to-mobile' })
    } else {
      props.setIsEmailRowDisplayed(false)
    }
  }
  // Taking ref from props which is triggered on enter keyDown
  useImperativeHandle(props.inputRef, () => ({
    click() {
      onPress()
    },
  }))

  return (
    <StyledButton
      ref={props.inputRef}
      styleType={props.isEmailValid ? undefined : 'invalid'}
      onPress={onPress}
      data-testid={TestIDs.SIGNUP.GET_STARTED_BUTTON}
    >
      {props.isSubmitting || isLoadingRemoteStrings ? (
        <Spinner height={20} width={20} white={true} />
      ) : (
        getRemoteString({ key: 'registration.get_started.cta' })
      )}
    </StyledButton>
  )
}

export const GetStartedButton = enhance(Button)
